import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotification } from './useNotification';
import { axiosInstance } from '../utils';

type queryKey = string[];

export const useHandleApiCall = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const fetchData = async (url: string, params = {}) => {
    const response = await axiosInstance.get(url, { params });
    return response.data;
  };

  const postData = async (url: string, data) => {
    const response = await axiosInstance.post(url, { ...data });
    return response.data;
  };

  const patchData = async (url: string, data) => {
    const response = await axiosInstance.patch(url, { ...data });
    return response.data;
  };

  const deleteData = async (url: string, id: string) => {
    const response = await axiosInstance.delete(`${url}${id}`);
    return response.data;
  };

  const invalidateMultipleQueries = (keys: queryKey) => {
    keys.forEach((key) => {
      queryClient.invalidateQueries([key]);
    });
  };

  const get = (
    key: queryKey,
    url: string,
    params = {},
    refetchInterval = 0,
    keyToInvalidate = [],
    enabled = true
  ) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery({
      queryKey: [...key],
      queryFn: () => fetchData(url, params),
      ...(refetchInterval && { refetchInterval }),
      enabled: enabled,
      onSuccess: () => {
        invalidateMultipleQueries(keyToInvalidate);
      },
    });
  };

  const post = (key: queryKey, url: string) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMutation({
      mutationFn: (data: any) => postData(url, data),
      onSuccess: async () => {
        invalidateMultipleQueries(key);
      },
      onError: () => {
        // handle error
      },
    });
  };

  const patch = (key: queryKey, url: string) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMutation({
      mutationFn: (data: any) => patchData(url, data),
      onSuccess: async () => {
        queryClient.invalidateQueries([...key]);
      },
      onError: () => {
        showNotification('Failed to update item', 'error');
      },
    });
  };

  const deleteReq = (key: queryKey, url: string) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMutation({
      mutationFn: (id: string) => deleteData(url, id),
      onSuccess: async () => {
        queryClient.invalidateQueries([...key]);
      },
      onError: () => {
        showNotification('Failed to delete item', 'error');
      },
    });
  };

  return { get, post, patch, deleteReq };
};
